<template>
  <aside
    v-if="renderContainer"
    :class="['adsterra-ad', `adsterra-ad--${resolvedKind}`, customClass, { 'adsterra-ad--confirmed-fill': confirmedFill }]"
    :aria-label="`${placementLabel} advertisement`"
  >
    <div class="adsterra-ad__label">Advertisement</div>
    <div ref="slot" class="adsterra-ad__slot"></div>
  </aside>
</template>

<script>
const AD_UNITS = {
  horizontalDesktop: {
    key: 'd50294ccf556a8a94b6388bb8897c95c',
    flag: '728x90',
    width: 728,
    height: 90,
    src: 'https://www.highperformanceformat.com/d50294ccf556a8a94b6388bb8897c95c/invoke.js'
  },
  rectangleMobile: {
    key: '337810d19f1c23c18bea15bbafa9db11',
    flag: '300x250',
    width: 300,
    height: 250,
    src: 'https://www.highperformanceformat.com/337810d19f1c23c18bea15bbafa9db11/invoke.js'
  },
  rail: {
    key: 'b218574784ca49e8d80eb29fd74cccc9',
    flag: '160x600',
    width: 160,
    height: 600,
    src: 'https://www.highperformanceformat.com/b218574784ca49e8d80eb29fd74cccc9/invoke.js'
  }
};
const ADSTERRA_ENABLED = process.env.VUE_APP_ADSTERRA_ENABLED === 'true';
const UNIT_FLAGS = {
  '728x90': process.env.VUE_APP_ADSTERRA_728X90_ENABLED === 'true',
  '160x600': process.env.VUE_APP_ADSTERRA_160X600_ENABLED === 'true',
  '300x250': process.env.VUE_APP_ADSTERRA_300X250_ENABLED === 'true'
};
let runtimeConfigPromise = null;

function loadRuntimeConfig() {
  if (!runtimeConfigPromise) {
    runtimeConfigPromise = fetch('/adsterra-config.json', { cache: 'no-store' })
      .then(response => (response.ok ? response.json() : null))
      .catch(() => null);
  }
  return runtimeConfigPromise;
}

export default {
  name: 'AdsterraBanner',
  props: {
    kind: {
      type: String,
      default: 'horizontal'
    },
    placement: {
      type: String,
      required: true
    },
    customClass: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loaded: false,
      unsafeRoute: false,
      resolvedKind: this.kind,
      unit: null,
      runtimeConfig: null,
      configReady: false,
      adDisplayReady: false,
      confirmedFill: false,
      noFill: false,
      probeToken: 0,
      noFillTimer: null
    };
  },
  computed: {
    renderContainer() {
      return this.adsEnabled && !this.unsafeRoute && !!this.unit && this.unitEnabled(this.unit) && this.adDisplayReady && !this.noFill;
    },
    adsEnabled() {
      if (!ADSTERRA_ENABLED || !this.configReady) {
        return false;
      }
      return !this.runtimeConfig || this.runtimeConfig.enabled === true;
    },
    placementLabel() {
      return this.placement.replace(/[-_]/g, ' ');
    }
  },
  mounted() {
    this.onResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.onResize);
    this.configureUnit();
    this.updateRouteSafety();
    loadRuntimeConfig().then(config => {
      this.runtimeConfig = config;
      this.configReady = true;
      this.configureUnit();
      this.prepareAd();
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    if (this.noFillTimer) {
      window.clearTimeout(this.noFillTimer);
    }
  },
  methods: {
    configureUnit() {
      if (this.kind === 'rail') {
        if (window.innerWidth < 1200) {
          this.unit = null;
          return;
        }
        this.resolvedKind = 'rail';
        this.unit = AD_UNITS.rail;
        return;
      }

      if (window.innerWidth < 768) {
        this.resolvedKind = 'rectangle-mobile';
        this.unit = AD_UNITS.rectangleMobile;
      } else {
        this.resolvedKind = 'horizontal-desktop';
        this.unit = AD_UNITS.horizontalDesktop;
      }
    },
    handleResize() {
      const previousKey = this.unit ? this.unit.key : '';
      this.configureUnit();
      const nextKey = this.unit ? this.unit.key : '';
      if (previousKey !== nextKey) {
        this.prepareAd();
      }
    },
    updateRouteSafety() {
      const routePath = this.$route && this.$route.path ? this.$route.path : '';
      const browserPath = window.location ? window.location.pathname : '';
      this.unsafeRoute = routePath.indexOf('/search/') === 0 || browserPath.indexOf('/search/') === 0;
    },
    clearAd() {
      this.loaded = false;
      if (this.$refs.slot) {
        this.$refs.slot.innerHTML = '';
      }
    },
    resetAdState() {
      this.probeToken += 1;
      this.adDisplayReady = false;
      this.confirmedFill = false;
      this.noFill = false;
      if (this.noFillTimer) {
        window.clearTimeout(this.noFillTimer);
        this.noFillTimer = null;
      }
      this.clearAd();
    },
    unitEnabled(unit) {
      if (!unit || !UNIT_FLAGS[unit.flag]) {
        return false;
      }
      if (!this.runtimeConfig || !this.runtimeConfig.units) {
        return false;
      }
      return this.runtimeConfig.units[unit.flag] === true;
    },
    prepareAd() {
      this.resetAdState();
      if (!this.adsEnabled || this.unsafeRoute || !this.unit || !this.unitEnabled(this.unit)) {
        return;
      }

      this.adDisplayReady = true;
      this.$nextTick(this.injectAd);
    },
    injectAd() {
      if (this.loaded || !this.renderContainer || !this.unit || !this.$refs.slot) {
        return;
      }

      const sandbox = document.createElement('iframe');
      sandbox.className = 'adsterra-ad__sandbox';
      sandbox.title = `${this.placementLabel} advertisement`;
      sandbox.width = String(this.unit.width);
      sandbox.height = String(this.unit.height);
      sandbox.setAttribute('sandbox', 'allow-scripts allow-same-origin');
      sandbox.setAttribute('scrolling', 'no');
      sandbox.setAttribute('loading', 'lazy');
      sandbox.dataset.viberaryAdPlacement = this.placement;
      const adDocument = [
        '<!doctype html><html><head><meta charset="utf-8">',
        `<style>html,body{margin:0;padding:0;width:${this.unit.width}px;height:${this.unit.height}px;overflow:hidden;background:transparent;}</style>`,
        '</head><body>',
        '<scr' + 'ipt>',
        `atOptions = {'key':'${this.unit.key}','format':'iframe','height':${this.unit.height},'width':${this.unit.width},'params':{}};`,
        '</scr' + 'ipt>',
        `<scr` + `ipt src="${this.unit.src}"></scr` + `ipt>`,
        '</body></html>',
      ].join('');
      sandbox.srcdoc = adDocument;

      this.clearAd();
      this.$refs.slot.appendChild(sandbox);
      this.loaded = true;
      this.scheduleNoFillCheck(sandbox, this.probeToken);
    },
    scheduleNoFillCheck(frame, token) {
      const checkDelays = [4000, 7000, 10000];
      let checkIndex = 0;
      const runCheck = () => {
        if (token !== this.probeToken || !frame) {
          return;
        }

        let hasVisibleCreative = false;
        try {
          const body = frame.contentDocument && frame.contentDocument.body;
          hasVisibleCreative = this.hasVisibleCreative(body);
        } catch (error) {
          hasVisibleCreative = true;
        }

        if (hasVisibleCreative) {
          this.confirmedFill = true;
          return;
        }

        checkIndex += 1;
        if (checkIndex < checkDelays.length) {
          this.noFillTimer = window.setTimeout(runCheck, checkDelays[checkIndex] - checkDelays[checkIndex - 1]);
          return;
        }

        this.noFill = true;
        this.adDisplayReady = false;
        this.clearAd();
        this.$forceUpdate();
      };
      this.noFillTimer = window.setTimeout(runCheck, checkDelays[0]);
    },
    hasVisibleCreative(body) {
      if (!body) {
        return false;
      }

      const candidates = Array.from(body.querySelectorAll('iframe,img,video,canvas,object,embed,a'));
      return candidates.some(element => {
        const style = element.ownerDocument.defaultView.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
          return false;
        }

        const rect = element.getBoundingClientRect();
        const width = Math.max(rect.width, element.offsetWidth || 0, Number(element.getAttribute('width')) || 0);
        const height = Math.max(rect.height, element.offsetHeight || 0, Number(element.getAttribute('height')) || 0);
        if (width < 20 || height < 20) {
          return false;
        }

        const tag = element.tagName.toLowerCase();
        if (tag === 'iframe') {
          // Adsterra can render a real creative in a document-written iframe
          // whose src remains empty/about:blank. Geometry is the reliable
          // signal here; zero-size/no-insert no-fill still collapses.
          return width >= 50 && height >= 40;
        }

        if (tag === 'a' && !element.getAttribute('href')) {
          return false;
        }

        return true;
      });
    }
  },
  watch: {
    '$route.fullPath'() {
      this.updateRouteSafety();
      if (this.unsafeRoute) {
        this.resetAdState();
        return;
      }
      this.configureUnit();
      this.prepareAd();
    }
  }
};
</script>

<style scoped>
.adsterra-ad {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  margin: 22px auto;
  max-width: 100%;
  text-align: center;
}
.adsterra-ad:not(.adsterra-ad--confirmed-fill) {
  height: 0 !important;
  min-height: 0 !important;
  margin: 0 auto;
  overflow: hidden;
  pointer-events: none;
}
.adsterra-ad:not(.adsterra-ad--confirmed-fill) .adsterra-ad__label {
  display: none;
}
.adsterra-ad:not(.adsterra-ad--confirmed-fill) .adsterra-ad__slot {
  height: 0 !important;
  min-height: 0 !important;
}
.adsterra-ad:not(.adsterra-ad--confirmed-fill) .adsterra-ad__sandbox {
  height: 0 !important;
}
.adsterra-ad__label {
  color: #777;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  line-height: 1;
  text-transform: uppercase;
}
.adsterra-ad__slot {
  align-items: center;
  display: flex;
  justify-content: center;
  max-width: 100%;
  overflow: hidden;
}
.adsterra-ad--horizontal-desktop .adsterra-ad__slot {
  min-height: 90px;
  width: 728px;
}
.adsterra-ad--rectangle-mobile .adsterra-ad__slot {
  min-height: 250px;
  width: 300px;
}
.adsterra-ad--rail {
  margin-top: 0;
  position: sticky;
  top: 96px;
}
.adsterra-ad--rail .adsterra-ad__slot {
  min-height: 600px;
  width: 160px;
}
.adsterra-ad__sandbox {
  border: 0;
  display: block;
  max-width: 100%;
  overflow: hidden;
}
@media (max-width: 767px) {
  .adsterra-ad {
    margin: 16px auto;
  }
}
</style>
